// src/stats/EncoursClients.jsx
import React, { useState, useEffect } from "react";
import {
    Card, Row, Col, Button, Table, Form, Spinner,
    Pagination, Tooltip, OverlayTrigger, Modal
} from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import { COLORS } from "../constants/colors";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";

/* ============================================================
   TOAST NOTIFICATION — Design charte
   ============================================================ */
const ToastNotification = ({ show, onClose, type, title, message, autoHideDelay = 5000 }) => {
    if (!show) return null;
    const config = {
        success: { bgColor: COLORS.success || '#10b981', icon: 'ri-checkbox-circle-fill' },
        error:   { bgColor: COLORS.danger  || '#ef4444', icon: 'ri-error-warning-fill' },
        info:    { bgColor: COLORS.info    || '#3b82f6', icon: 'ri-information-fill' },
        warning: { bgColor: COLORS.warning || '#f59e0b', icon: 'ri-alert-fill' }
    }[type] || { bgColor: COLORS.info || '#3b82f6', icon: 'ri-information-fill' };

    return (
        <div className="toast-notification-fixed">
            <div className="toast-modern">
                <div className="toast-header-custom" style={{ background: config.bgColor }}>
                    <div className="d-flex align-items-center gap-2">
                        <i className={`${config.icon} text-white`} style={{ fontSize: '1.5rem' }}></i>
                        <strong className="text-white fs-6">{title}</strong>
                    </div>
                    <button onClick={onClose} className="toast-close-btn">
                        <i className="ri-close-line fs-5"></i>
                    </button>
                </div>
                <div className="toast-body-custom"><p className="mb-0">{message}</p></div>
                <div className="toast-progress" style={{ background: config.bgColor, animation: `shrink ${autoHideDelay}ms linear forwards` }}></div>
            </div>
        </div>
    );
};

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function EncoursClients() {
    const navigate = useNavigate();
    const [resume, setResume] = useState(null);
    const [encours, setEncours] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterText, setFilterText] = useState('');
    const [filterRetard, setFilterRetard] = useState('tous');
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [clientDetail, setClientDetail] = useState(null);
    const [facturesClient, setFacturesClient] = useState([]);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', title: '', message: '' });

    const showToast = (type, title, message) => setToast({ show: true, type, title, message });
    const hideToast = () => setToast({ show: false, type: '', title: '', message: '' });

    useEffect(() => {
        fetchDonnees();
    }, [filterRetard]);

    /* ============================================================
       FETCH
       ============================================================ */
    const fetchDonnees = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");

            const resumeResponse = await fetch(
                `${API_BASE_URL}/finances.php?action=resume_encours`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            let url = `${API_BASE_URL}/finances.php?action=encours_clients`;
            const params = new URLSearchParams();
            if (filterText) params.append('recherche', filterText);
            if (filterRetard !== 'tous') params.append('retard', filterRetard);
            if (params.toString()) url += '&' + params.toString();

            const encoursResponse = await fetch(url, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!resumeResponse.ok || !encoursResponse.ok) {
                throw new Error("Erreur chargement des données");
            }

            const resumeResult = await resumeResponse.json();
            const encoursResult = await encoursResponse.json();

            setResume(resumeResult.data);
            setEncours(encoursResult.data || []);
            setCurrentPage(1);
        } catch (err) {
            setError(err.message);
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchDetailClient = async (idClient) => {
        setLoadingDetail(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/finances.php?action=detail_encours&id_client=${idClient}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (!response.ok) throw new Error("Erreur chargement");
            const result = await response.json();
            setFacturesClient(result.data || []);
            setShowDetailModal(true);
        } catch (err) {
            showToast('error', 'Erreur', err.message);
        } finally {
            setLoadingDetail(false);
        }
    };

    /* ============================================================
       HELPERS
       ============================================================ */
    const totalPages = Math.ceil(encours.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentItems = encours.slice(startIndex, startIndex + itemsPerPage);

    const getRetardBadge = (jours) => {
        if (jours < 0) {
            return (
                <span className="status-badge-charte" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                    <i className="ri-time-line"></i>À venir
                </span>
            );
        } else if (jours <= 30) {
            return (
                <span className="status-badge-charte" style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)' }}>
                    <i className="ri-alert-line"></i>{jours}j
                </span>
            );
        } else if (jours <= 60) {
            return (
                <span className="status-badge-charte" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}>
                    <i className="ri-error-warning-line"></i>{jours}j
                </span>
            );
        } else {
            return (
                <span className="status-badge-charte" style={{ background: 'linear-gradient(135deg, #dc2626, #991b1b)' }}>
                    <i className="ri-alarm-warning-line"></i>+60j
                </span>
            );
        }
    };

    /* ============================================================
       LOADING — identique à l'original
       ============================================================ */
    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center" style={{ maxWidth: '400px' }}>
                            <div className="loading-ring-wrapper mb-4">
                                <div className="loading-ring"></div>
                                <div className="loading-ring-inner"></div>
                                <i className="ri-loader-4-fill loading-icon"></i>
                            </div>
                            <h5 className="fw-bold" style={{ color: COLORS.primaryDark, letterSpacing: '0.3px' }}>
                                Chargement des encours clients...
                            </h5>
                            <p className="text-muted small mb-0">Veuillez patienter quelques instants...</p>
                            <div className="mx-auto mt-4" style={{ width: '80%' }}>
                                <div className="progress" style={{ height: '3px', background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                                    <div
                                        className="progress-bar progress-bar-striped progress-bar-animated"
                                        style={{
                                            width: '100%',
                                            background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary}, ${COLORS.primary})`,
                                            backgroundSize: '200% 100%',
                                            animation: 'shimmer 1.5s linear infinite'
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
                <style>{`
                    .loading-ring-wrapper { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; border: 4px solid rgba(33,172,205,0.15); border-top-color: ${COLORS.primary}; animation: spin 1s linear infinite; }
                    .loading-ring-inner { position: absolute; top: 8px; left: 8px; right: 8px; bottom: 8px; border-radius: 50%; border: 3px solid rgba(33,172,205,0.08); border-bottom-color: ${COLORS.secondary}; animation: spin 0.8s linear infinite reverse; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: ${COLORS.primary}; animation: pulse-icon 1.2s ease-in-out infinite; }
                    @keyframes spin { to { transform: rotate(360deg); } }
                    @keyframes pulse-icon { 0%,100% { transform: translate(-50%,-50%) scale(1); opacity:1; } 50% { transform: translate(-50%,-50%) scale(1.15); opacity:0.6; } }
                    @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
                `}</style>
            </>
        );
    }

    /* ============================================================
       RENDU PRINCIPAL
       ============================================================ */
    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>

                <ToastNotification
                    show={toast.show}
                    onClose={hideToast}
                    type={toast.type}
                    title={toast.title}
                    message={toast.message}
                />

                <div className="container-fluid">

                    {/* ===== EN-TÊTE MODERNE ===== */}
                    <div className="header-modern mb-4">
                        <div className="header-left">
                            <div className="header-icon-wrapper">
                                <i className="ri-bank-card-line"></i>
                            </div>
                            <div>
                                <h5 className="header-title">Encours clients</h5>
                                <div className="d-flex flex-wrap gap-2 mt-1">
                                    <span className="badge-modern">
                                        <i className="ri-group-line"></i> {encours.length} clients
                                    </span>
                                    {resume && (
                                        <span className="badge-modern" style={{ background: `${COLORS.danger}15`, color: COLORS.danger }}>
                                            <i className="ri-money-euro-circle-line"></i> {formatCurrency(resume.total?.total_creances || 0)}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="d-flex gap-2 flex-wrap">
                            <Button className="btn-modern-outline" onClick={fetchDonnees} disabled={loading}>
                                <i className={`ri-refresh-line ${loading ? 'spin-icon' : ''}`}></i> Actualiser
                            </Button>
                            <Button className="btn-modern-outline" onClick={() => navigate('/stats/dashboard')}>
                                <i className="ri-arrow-left-line"></i> Retour
                            </Button>
                        </div>
                    </div>

                    {/* ===== KPI CARDS ===== */}
                    {resume && (
                        <Row className="g-3 mb-4">
                            <Col sm="6" xl="4">
                                <div className="kpi-charte kpi-danger">
                                    <div className="kpi-icon-charte"><i className="ri-money-euro-circle-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Total créances</span>
                                        <h3 className="kpi-value-charte">{formatCurrency(resume.total?.total_creances || 0)}</h3>
                                        <span className="kpi-unit-charte">{resume.total?.nb_factures_impayees || 0} factures</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="4">
                                <div className="kpi-charte kpi-warning">
                                    <div className="kpi-icon-charte"><i className="ri-group-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Clients débiteurs</span>
                                        <h3 className="kpi-value-charte">{resume.total?.nb_clients_debiteurs || 0}</h3>
                                        <span className="kpi-unit-charte">avec encours</span>
                                    </div>
                                </div>
                            </Col>
                            <Col sm="6" xl="4">
                                <div className="kpi-charte kpi-info">
                                    <div className="kpi-icon-charte"><i className="ri-calculator-line"></i></div>
                                    <div className="kpi-content-charte">
                                        <span className="kpi-label-charte">Encours moyen</span>
                                        <h3 className="kpi-value-charte" style={{ fontSize: '1rem' }}>
                                            {formatCurrency((resume.total?.total_creances || 0) / (resume.total?.nb_clients_debiteurs || 1))}
                                        </h3>
                                        <span className="kpi-unit-charte">par client</span>
                                    </div>
                                </div>
                            </Col>
                        </Row>
                    )}

                    {/* ===== RÉPARTITION PAR ANCIENNETÉ ===== */}
                    {resume && resume.anciennete && (
                        <Card className="border-0 shadow-sm rounded-3 mb-4">
                            <Card.Header className="card-header-gradient">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-timer-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Répartition par ancienneté</span>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-4">
                                <Row className="g-3">
                                    <Col md="3" sm="6">
                                        <div className="stat-card-mini-charte" style={{ borderLeftColor: COLORS.success }}>
                                            <span className="stat-card-label-charte">À venir</span>
                                            <h5 className="stat-card-value-charte" style={{ color: COLORS.success }}>
                                                {formatCurrency(resume.anciennete.a_venir || 0)}
                                            </h5>
                                        </div>
                                    </Col>
                                    <Col md="3" sm="6">
                                        <div className="stat-card-mini-charte" style={{ borderLeftColor: COLORS.warning }}>
                                            <span className="stat-card-label-charte">1-30 jours</span>
                                            <h5 className="stat-card-value-charte" style={{ color: COLORS.warning }}>
                                                {formatCurrency(resume.anciennete.retard_30j || 0)}
                                            </h5>
                                        </div>
                                    </Col>
                                    <Col md="3" sm="6">
                                        <div className="stat-card-mini-charte" style={{ borderLeftColor: COLORS.danger }}>
                                            <span className="stat-card-label-charte">31-60 jours</span>
                                            <h5 className="stat-card-value-charte" style={{ color: COLORS.danger }}>
                                                {formatCurrency(resume.anciennete.retard_60j || 0)}
                                            </h5>
                                        </div>
                                    </Col>
                                    <Col md="3" sm="6">
                                        <div className="stat-card-mini-charte" style={{ borderLeftColor: '#991b1b' }}>
                                            <span className="stat-card-label-charte">+60 jours</span>
                                            <h5 className="stat-card-value-charte" style={{ color: '#991b1b' }}>
                                                {formatCurrency(resume.anciennete.retard_plus_60j || 0)}
                                            </h5>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}

                    {/* ===== FILTRES ===== */}
                    <Card className="filter-card-charte mb-4">
                        <Card.Body className="py-3 px-4">
                            <Row className="align-items-end g-3">
                                <Col md="4">
                                    <Form.Group>
                                        <Form.Label className="form-label-modern text-white">
                                            <i className="ri-search-line me-2"></i>
                                            Recherche
                                        </Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Client, code..."
                                            value={filterText}
                                            onChange={(e) => setFilterText(e.target.value)}
                                            className="filter-select-charte"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md="3">
                                    <Form.Group>
                                        <Form.Label className="form-label-modern text-white">
                                            <i className="ri-filter-line me-2"></i>
                                            Filtre retard
                                        </Form.Label>
                                        <Form.Select
                                            value={filterRetard}
                                            onChange={(e) => setFilterRetard(e.target.value)}
                                            className="filter-select-charte"
                                        >
                                            <option value="tous">Tous</option>
                                            <option value="a_venir">À venir</option>
                                            <option value="retard_30">1-30 jours</option>
                                            <option value="retard_60">31-60 jours</option>
                                            <option value="retard_plus">+60 jours</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md="3">
                                    <Form.Group>
                                        <Form.Label className="form-label-modern text-white">
                                            <i className="ri-list-settings-line me-2"></i>
                                            Affichage
                                        </Form.Label>
                                        <Form.Select
                                            value={itemsPerPage}
                                            onChange={(e) => {
                                                setItemsPerPage(parseInt(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            className="filter-select-charte"
                                        >
                                            <option value="5">5 par page</option>
                                            <option value="10">10 par page</option>
                                            <option value="20">20 par page</option>
                                            <option value="50">50 par page</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md="2">
                                    <Button className="btn-modern-primary w-100 justify-content-center" onClick={fetchDonnees}>
                                        <i className="ri-refresh-line"></i>
                                    </Button>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* ===== TABLEAU ===== */}
                    <Card className="border-0 shadow-sm rounded-3 overflow-hidden">
                        <Card.Header className="card-header-gradient">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="d-flex align-items-center gap-2">
                                    <i className="ri-group-line text-white fs-5"></i>
                                    <span className="fw-semibold text-white">Détail des encours</span>
                                    <span className="badge-header-light">{encours.length} clients</span>
                                </div>
                            </div>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Client</th>
                                            <th className="text-center">Factures</th>
                                            <th className="text-end">Encours total</th>
                                            <th>Dernière facture</th>
                                            <th>Échéance</th>
                                            <th className="text-center">Retard</th>
                                            <th className="text-center pe-4">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentItems.map((client) => {
                                            const joursRetard = client.jours_retard_max || 0;
                                            return (
                                                <tr key={client.id_client}>
                                                    <td className="ps-4">
                                                        <div className="d-flex align-items-center gap-2">
                                                            <div className="avatar-icon-charte">
                                                                <i className="ri-user-3-line"></i>
                                                            </div>
                                                            <div>
                                                                <Link to={`/ventes/clients/${client.id_client}`} className="table-link-charte">
                                                                    {client.raison_sociale}
                                                                </Link>
                                                                <div>
                                                                    <small className="text-muted">{client.code_client}</small>
                                                                    {client.telephone && (
                                                                        <small className="text-muted ms-2">
                                                                            <i className="ri-phone-line me-1"></i>
                                                                            {client.telephone}
                                                                        </small>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td className="text-center">
                                                        <span className="quantity-badge-charte">{client.nb_factures}</span>
                                                    </td>
                                                    <td className="text-end fw-bold" style={{ color: COLORS.danger }}>
                                                        {formatCurrency(client.total_encours)}
                                                    </td>
                                                    <td>
                                                        <span className="text-muted">
                                                            <i className="ri-calendar-line me-1"></i>
                                                            {formatDate(client.derniere_facture)}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className="text-muted">
                                                            <i className="ri-calendar-check-line me-1"></i>
                                                            {formatDate(client.derniere_echeance)}
                                                        </span>
                                                    </td>
                                                    <td className="text-center">
                                                        {getRetardBadge(joursRetard)}
                                                    </td>
                                                    <td className="text-center pe-4">
                                                        <div className="d-flex gap-2 justify-content-center">
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Voir le détail</Tooltip>}>
                                                                <button
                                                                    className="action-btn-charte"
                                                                    onClick={() => {
                                                                        setClientDetail(client);
                                                                        fetchDetailClient(client.id_client);
                                                                    }}
                                                                    style={{ '--btn-color': COLORS.primary }}
                                                                >
                                                                    <i className="ri-eye-line"></i>
                                                                </button>
                                                            </OverlayTrigger>
                                                            <OverlayTrigger placement="top" overlay={<Tooltip>Saisir un règlement</Tooltip>}>
                                                                <button
                                                                    className="action-btn-charte"
                                                                    onClick={() => navigate(`/ventes/NouveauReglement?client=${client.id_client}`)}
                                                                    style={{ '--btn-color': COLORS.success }}
                                                                >
                                                                    <i className="ri-bank-card-line"></i>
                                                                </button>
                                                            </OverlayTrigger>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        {currentItems.length === 0 && (
                                            <tr>
                                                <td colSpan="7">
                                                    <div className="empty-state-charte">
                                                        <div className="empty-state-icon-charte">
                                                            <i className="ri-inbox-line"></i>
                                                        </div>
                                                        <h6 className="fw-semibold mb-1">Aucun client trouvé</h6>
                                                        <p className="text-muted small mb-0">Aucun encours client pour cette période</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </Card.Body>
                    </Card>

                    {/* ===== PAGINATION ===== */}
                    {totalPages > 1 && !loading && (
                        <div className="pagination-wrapper-charte mt-3">
                            <div className="pagination-info-charte">
                                Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, encours.length)}</strong> sur <strong>{encours.length}</strong> clients
                            </div>
                            <Pagination className="pagination-charte mb-0">
                                <Pagination.Prev onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} />
                                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                                    let pageNum;
                                    if (totalPages <= 5) pageNum = i + 1;
                                    else if (currentPage <= 3) pageNum = i + 1;
                                    else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                                    else pageNum = currentPage - 2 + i;
                                    return (
                                        <Pagination.Item key={i} active={pageNum === currentPage} onClick={() => setCurrentPage(pageNum)}>
                                            {pageNum}
                                        </Pagination.Item>
                                    );
                                })}
                                <Pagination.Next onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages} />
                            </Pagination>
                        </div>
                    )}
                </div>

                {/* ===== MODAL DÉTAIL ===== */}
                <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered className="modal-charte">
                    <Modal.Header closeButton className="modal-header-charte" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                        <div className="d-flex align-items-center gap-3">
                            <div className="modal-header-icon"><i className="ri-file-list-line"></i></div>
                            <div>
                                <Modal.Title className="fs-5 fw-bold text-white mb-0">Factures impayées</Modal.Title>
                                <p className="text-white-50 mb-0 small">{clientDetail?.raison_sociale}</p>
                            </div>
                        </div>
                    </Modal.Header>
                    <Modal.Body className="p-4">
                        {loadingDetail ? (
                            <div className="text-center py-4">
                                <Spinner animation="border" style={{ color: COLORS.primary }} />
                            </div>
                        ) : facturesClient.length === 0 ? (
                            <div className="empty-state-charte">
                                <div className="empty-state-icon-charte">
                                    <i className="ri-checkbox-circle-line"></i>
                                </div>
                                <h6 className="fw-semibold mb-1">Aucune facture impayée</h6>
                            </div>
                        ) : (
                            <div className="table-responsive">
                                <Table hover className="align-middle mb-0 table-charte">
                                    <thead>
                                        <tr>
                                            <th className="ps-4">Facture</th>
                                            <th>Date</th>
                                            <th>Échéance</th>
                                            <th className="text-end">Montant</th>
                                            <th className="text-end">Réglé</th>
                                            <th className="text-end">Solde</th>
                                            <th className="text-center">Retard</th>
                                            <th className="text-center pe-4">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {facturesClient.map(f => (
                                            <tr key={f.id_facture}>
                                                <td className="ps-4">
                                                    <Link to={`/ventes/facturesDetails/${f.id_facture}`} className="table-link-charte">
                                                        {f.numero_facture}
                                                    </Link>
                                                </td>
                                                <td className="text-muted">{formatDate(f.date_facture)}</td>
                                                <td className={f.jours_retard > 0 ? 'text-danger fw-bold' : 'text-muted'}>
                                                    {formatDate(f.date_echeance)}
                                                </td>
                                                <td className="text-end">{formatCurrency(f.montant_ttc)}</td>
                                                <td className="text-end" style={{ color: COLORS.success }}>
                                                    {formatCurrency(f.montant_regle)}
                                                </td>
                                                <td className="text-end fw-bold" style={{ color: COLORS.danger }}>
                                                    {formatCurrency(f.solde)}
                                                </td>
                                                <td className="text-center">
                                                    {f.jours_retard > 0 ? (
                                                        <span className="status-badge-charte" style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)' }}>
                                                            <i className="ri-alert-line"></i>{f.jours_retard}j
                                                        </span>
                                                    ) : (
                                                        <span className="status-badge-charte" style={{ background: 'linear-gradient(135deg, #10b981, #059669)' }}>
                                                            <i className="ri-check-line"></i>OK
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="text-center pe-4">
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Saisir règlement</Tooltip>}>
                                                        <button
                                                            className="action-btn-charte"
                                                            onClick={() => navigate(`/ventes/NouveauReglement?facture=${f.id_facture}&client=${clientDetail.id_client}`)}
                                                            style={{ '--btn-color': COLORS.success }}
                                                        >
                                                            <i className="ri-bank-card-line"></i>
                                                        </button>
                                                    </OverlayTrigger>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer className="border-0 pb-4 px-4 pt-0">
                        <Button className="btn-modern-outline" onClick={() => setShowDetailModal(false)}>
                            Fermer
                        </Button>
                        <Button className="btn-modern-primary" onClick={() => navigate(`/ventes/NouveauReglement?client=${clientDetail?.id_client}`)}>
                            <i className="ri-bank-card-line me-2"></i>
                            Saisir un règlement
                        </Button>
                    </Modal.Footer>
                </Modal>
                <Footer />
            </div>

            {/* ===== STYLES CHARTE ===== */}
            <style>{`
                :root {
                    --primary: ${COLORS.primary || '#295AA7'};
                    --primary-dark: ${COLORS.primaryDark || '#1e3f7a'};
                    --secondary: ${COLORS.secondary || '#21ACCD'};
                    --success: ${COLORS.success || '#10b981'};
                    --danger: ${COLORS.danger || '#ef4444'};
                    --warning: ${COLORS.warning || '#f59e0b'};
                    --info: ${COLORS.info || '#3b82f6'};
                }

                /* ===== EN-TÊTE ===== */
                .header-modern {
                    background: white; padding: 1.2rem 1.8rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid var(--secondary);
                    border-radius: 12px;
                    display: flex; align-items: center; justify-content: space-between;
                    flex-wrap: wrap; gap: 1rem;
                }
                .header-left { display: flex; align-items: center; gap: 1rem; }
                .header-icon-wrapper {
                    width: 52px; height: 52px;
                    background: linear-gradient(135deg, var(--primary), var(--secondary));
                    display: flex; align-items: center; justify-content: center;
                    border-radius: 14px; color: white; font-size: 1.5rem;
                    box-shadow: 0 4px 12px rgba(41, 90, 167, 0.25);
                }
                .header-title { color: #1e293b; font-size: 1.3rem; font-weight: 700; margin: 0; }
                .badge-modern {
                    background: #f1f4f9; color: #475569;
                    padding: 0.3rem 0.8rem; border-radius: 50px;
                    font-size: 0.75rem; font-weight: 600;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .badge-modern i { color: var(--secondary); }
                .spin-icon { animation: spin 1s linear infinite; }

                /* ===== FILTRES ===== */
                .filter-card-charte {
                    background: linear-gradient(135deg, var(--primary-dark), var(--primary));
                    border-radius: 12px; border: none;
                    box-shadow: 0 8px 24px rgba(41,90,167,0.15);
                }
                .form-label-modern {
                    font-weight: 600; font-size: 0.8rem;
                    margin-bottom: 0.4rem;
                    text-transform: uppercase; letter-spacing: 0.3px;
                }
                .filter-select-charte {
                    background: white !important;
                    border: none !important;
                    border-radius: 50px !important;
                    padding: 0.6rem 1rem !important;
                    font-size: 0.9rem;
                    cursor: pointer;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
                }
                .filter-select-charte:focus { box-shadow: 0 0 0 3px rgba(255,255,255,0.2) !important; }

                /* ===== KPI ===== */
                .kpi-charte {
                    background: white; border-radius: 12px;
                    padding: 1.2rem;
                    display: flex; align-items: center; gap: 1rem;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    border-left: 4px solid;
                    transition: all 0.25s ease;
                    height: 100%;
                }
                .kpi-charte:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 12px 28px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: var(--primary); }
                .kpi-success { border-left-color: var(--success); }
                .kpi-danger  { border-left-color: var(--danger); }
                .kpi-info    { border-left-color: var(--info); }
                .kpi-warning { border-left-color: var(--warning); }
                .kpi-icon-charte {
                    width: 48px; height: 48px; border-radius: 12px;
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.4rem; flex-shrink: 0;
                }
                .kpi-primary .kpi-icon-charte { background: ${COLORS.primary}15; color: var(--primary); }
                .kpi-success .kpi-icon-charte { background: ${COLORS.success}15; color: var(--success); }
                .kpi-danger  .kpi-icon-charte { background: ${COLORS.danger}15; color: var(--danger); }
                .kpi-info    .kpi-icon-charte { background: ${COLORS.info}15; color: var(--info); }
                .kpi-warning .kpi-icon-charte { background: ${COLORS.warning}15; color: var(--warning); }
                .kpi-content-charte { flex: 1; min-width: 0; }
                .kpi-label-charte {
                    font-size: 0.7rem; color: #94a3b8;
                    text-transform: uppercase; font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value-charte {
                    font-size: 1.35rem; font-weight: 700;
                    margin: 0.3rem 0; color: #1e293b;
                }
                .kpi-unit-charte { font-size: 0.7rem; color: #94a3b8; }

                /* ===== STAT CARD MINI ===== */
                .stat-card-mini-charte {
                    background: #f8fafc;
                    padding: 1rem 1.25rem;
                    border-radius: 12px;
                    border: 1px solid #e2e8f0;
                    border-left: 4px solid var(--primary);
                    transition: all 0.2s ease;
                    height: 100%;
                }
                .stat-card-mini-charte:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(0,0,0,0.06);
                }
                .stat-card-label-charte {
                    font-size: 0.7rem;
                    color: #64748b;
                    text-transform: uppercase;
                    font-weight: 600;
                    letter-spacing: 0.3px;
                    display: block;
                    margin-bottom: 0.25rem;
                }
                .stat-card-value-charte {
                    font-weight: 700;
                    margin: 0;
                    font-size: 1rem;
                }

                /* ===== HEADER GRADIENT ===== */
                .card-header-gradient {
                    background: linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%);
                    border-top-left-radius: 12px !important;
                    border-top-right-radius: 12px !important;
                    padding: 1rem 1.5rem;
                    border: none;
                }
                .badge-header-light {
                    background: rgba(255,255,255,0.2);
                    color: white;
                    border: 1px solid rgba(255,255,255,0.3);
                    border-radius: 50px;
                    padding: 0.25rem 0.75rem;
                    font-size: 0.75rem;
                    font-weight: 600;
                }

                /* ===== TABLE ===== */
                .table-charte thead th {
                    background: #f8fafc; color: #475569;
                    font-weight: 600; font-size: 0.7rem;
                    text-transform: uppercase; letter-spacing: 0.5px;
                    border-bottom: 2px solid #e2e8f0;
                    padding: 0.9rem 0.75rem;
                }
                .table-charte tbody td {
                    padding: 0.9rem 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                    font-size: 0.85rem;
                }
                .table-charte tbody tr { transition: background 0.15s ease; }
                .table-charte tbody tr:hover { background: #f8fafc; }
                .table-link-charte {
                    color: var(--primary);
                    text-decoration: none;
                    font-weight: 600;
                    font-size: 0.85rem;
                }
                .table-link-charte:hover { text-decoration: underline; }
                .quantity-badge-charte {
                    display: inline-flex; align-items: center; justify-content: center;
                    min-width: 45px; padding: 0.25rem 0.6rem;
                    background: #f1f5f9; border-radius: 12px;
                    font-weight: 600; font-size: 0.8rem; color: #475569;
                }
                .status-badge-charte {
                    display: inline-flex; align-items: center; gap: 4px;
                    color: white; padding: 0.3rem 0.75rem;
                    border-radius: 50px; font-size: 0.7rem; font-weight: 600;
                    white-space: nowrap;
                }

                /* ===== AVATAR ===== */
                .avatar-icon-charte {
                    width: 40px; height: 40px;
                    background: ${COLORS.primary}15;
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    color: var(--primary);
                    flex-shrink: 0;
                    font-size: 1.2rem;
                }

                /* ===== ACTION BTN ===== */
                .action-btn-charte {
                    width: 36px; height: 36px; border-radius: 10px;
                    display: inline-flex; align-items: center; justify-content: center;
                    background: white; border: 1.5px solid #e2e8f0;
                    color: #64748b; font-size: 1rem;
                    transition: all 0.2s ease;
                    cursor: pointer;
                }
                .action-btn-charte:hover {
                    border-color: var(--btn-color); color: var(--btn-color);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(41, 90, 167, 0.15);
                }

                /* ===== EMPTY STATE ===== */
                .empty-state-charte { text-align: center; padding: 3rem 2rem; }
                .empty-state-icon-charte {
                    width: 72px; height: 72px;
                    background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
                    border-radius: 50%;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 1rem; font-size: 1.8rem; color: #94a3b8;
                }

                /* ===== PAGINATION ===== */
                .pagination-wrapper-charte {
                    display: flex; justify-content: space-between; align-items: center;
                    padding: 1rem 1.5rem;
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.04);
                    flex-wrap: wrap; gap: 1rem;
                }
                .pagination-info-charte { font-size: 0.8rem; color: #64748b; }
                .pagination-charte { gap: 6px; }
                .pagination-charte .page-item .page-link {
                    border-radius: 10px;
                    border: 1.5px solid #e2e8f0;
                    color: #1e293b;
                    padding: 0.5rem 0.9rem;
                    font-weight: 500; font-size: 0.85rem;
                    transition: all 0.2s;
                }
                .pagination-charte .page-item.active .page-link {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border-color: var(--primary);
                    color: white;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                }
                .pagination-charte .page-item .page-link:hover {
                    background: #f1f5f9;
                    border-color: var(--secondary);
                    color: var(--primary);
                    transform: translateY(-1px);
                }

                /* ===== MODAL ===== */
                .modal-charte .modal-content {
                    border: none; border-radius: 20px; overflow: hidden;
                    box-shadow: 0 30px 80px rgba(0,0,0,0.15);
                }
                .modal-header-charte { padding: 1.25rem 1.5rem; border: none; }
                .modal-header-charte .btn-close { filter: brightness(0) invert(1); opacity: 0.8; }
                .modal-header-icon {
                    width: 48px; height: 48px; border-radius: 12px;
                    background: rgba(255,255,255,0.2);
                    display: flex; align-items: center; justify-content: center;
                    font-size: 1.5rem; color: white;
                }

                /* ===== BOUTONS ===== */
                .btn-modern-outline {
                    border: 1.5px solid #d1d5db; background: white;
                    padding: 0.5rem 1.4rem; border-radius: 50px;
                    font-size: 0.85rem; font-weight: 500; color: #4b5563;
                    transition: all 0.2s ease;
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-outline:hover {
                    border-color: var(--secondary); color: var(--secondary);
                    background: #f0fbff; transform: translateY(-1px);
                    box-shadow: 0 4px 8px rgba(33,172,205,0.15);
                }
                .btn-modern-primary {
                    background: linear-gradient(135deg, var(--primary), var(--primary-dark));
                    border: none; color: white; padding: 0.5rem 1.6rem;
                    border-radius: 50px; font-weight: 600; font-size: 0.85rem;
                    transition: all 0.2s ease;
                    box-shadow: 0 4px 12px rgba(41,90,167,0.3);
                    display: inline-flex; align-items: center; gap: 6px;
                }
                .btn-modern-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 18px rgba(41,90,167,0.4); color: white;
                }
                .btn-modern-outline:disabled,
                .btn-modern-primary:disabled { opacity: 0.6; transform: none; }

                /* ===== TOAST ===== */
                .toast-notification-fixed {
                    position: fixed; bottom: 24px; right: 24px;
                    z-index: 9999; animation: slideInRight 0.3s ease-out;
                }
                .toast-modern {
                    background: white; border-radius: 16px;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
                    min-width: 320px; max-width: 400px; overflow: hidden;
                }
                .toast-header-custom {
                    padding: 12px 20px;
                    display: flex; align-items: center; justify-content: space-between;
                }
                .toast-close-btn {
                    background: transparent; border: none;
                    color: white; cursor: pointer; opacity: 0.8;
                }
                .toast-close-btn:hover { opacity: 1; }
                .toast-body-custom { padding: 16px 20px; }
                .toast-body-custom p { color: #1e293b; font-size: 0.9rem; }
                .toast-progress { height: 3px; width: 100%; animation: shrink linear forwards; }

                /* ===== ANIMATIONS ===== */
                @keyframes spin { to { transform: rotate(360deg); } }
                @keyframes slideInRight {
                    from { opacity: 0; transform: translateX(100%); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes shrink {
                    from { width: 100%; }
                    to { width: 0%; }
                }

                /* ===== RESPONSIVE ===== */
                @media (max-width: 768px) {
                    .header-modern { padding: 1rem; }
                    .header-icon-wrapper { width: 44px; height: 44px; font-size: 1.2rem; }
                    .header-title { font-size: 1.1rem; }
                    .pagination-wrapper-charte { flex-direction: column; align-items: stretch; }
                    .pagination-info-charte { text-align: center; }
                    .toast-notification-fixed { bottom: 12px; right: 12px; left: 12px; }
                    .toast-modern { min-width: auto; max-width: none; }
                }
                .rounded-3 { border-radius: 12px !important; }
            `}</style>
        </>
    );
}
